import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CreateTimeEntryDto,
  TimeEntriesService,
  TimesheetEntryDto,
  WorkItemDto,
  WorkItemsService,
  WorkspaceUserDto,
  WorkspacesService, WorkItemFormDto,
} from '../../../../api';
import { AuthStore } from '../../../../auth/services/auth.store';
import { PageWrapperComponent } from '../../../../shared/pages/page-wrapper/page-wrapper.component';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { ModalService } from '../../../../shared/services/modal.service';
import { DateTimeUtils } from '../../../../shared/utils/date-time-utils';
import { WorkItemsFacadeService } from '../../../work-items/services/work-items-facade.service';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Divider } from 'primeng/divider';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TimeTrackerDayModalComponent } from '../../components/time-tracker-day-modal/time-tracker-day-modal.component';
import { TimeTrackerDayModalData } from '../../model/time-tracker-day-modal-data';

interface MonthEntryGroup {
  dayKey: string;
  dayLabel: string;
  dayTotalDuration: number;
  dayTotalRevenue: number;
  dayTotalEarnings: number;
  entries: TimesheetEntryDto[];
}

interface WorkItemFilterOption {
  id: string | null;
  name: string;
  clientName?: string | null;
}

@Component({
  selector: 'app-time-tracker-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PageWrapperComponent,
    Button,
    Card,
    Divider,
    InputNumber,
    InputText,
    Select,
    Tooltip,
  ],
  templateUrl: './time-tracker-page.component.html',
  styleUrl: './time-tracker-page.component.css',
})
export class TimeTrackerPageComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly fb = inject(FormBuilder);
  private readonly timeEntriesService = inject(TimeEntriesService);
  private readonly workItemsService = inject(WorkItemsService);
  private readonly workItemsFacadeService = inject(WorkItemsFacadeService);
  private readonly workspacesService = inject(WorkspacesService);
  private readonly modalService = inject(ModalService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly destroy$ = new Subject<void>();

  readonly ownerMode = this.authStore.isOwner;
  readonly hydrated = signal(false);
  readonly loadingData = signal(false);
  readonly savingQuickAdd = signal(false);
  readonly monthDate = signal<Date>(this.startOfMonth(new Date()));
  readonly timeEntries = signal<TimesheetEntryDto[]>([]);
  readonly workItems = signal<WorkItemDto[]>([]);
  readonly workspaceUsers = signal<WorkspaceUserDto[]>([]);
  readonly selectedUserId = signal<string | null>(null);
  readonly selectedWorkItemId = signal<string | null>(null);

  readonly activeWorkspaceId = computed(() => this.authStore.defaultWorkspace()?.id!);
  readonly currentUserId = computed(() => this.authStore.user()?.id ?? null);
  readonly selectedUser = computed(
    () => this.workspaceUsers().find((user) => user.id === this.selectedUserId()) ?? null,
  );
  readonly pageTitle = computed(() =>
    this.ownerMode() ? 'Tracker - widok właściciela' : 'Tracker',
  );
  readonly monthLabel = computed(() =>
    this.monthDate().toLocaleDateString('pl-PL', {
      month: 'long',
      year: 'numeric',
    }),
  );
  readonly monthOptions = [
    { label: 'Styczeń', value: 0 },
    { label: 'Luty', value: 1 },
    { label: 'Marzec', value: 2 },
    { label: 'Kwiecień', value: 3 },
    { label: 'Maj', value: 4 },
    { label: 'Czerwiec', value: 5 },
    { label: 'Lipiec', value: 6 },
    { label: 'Sierpień', value: 7 },
    { label: 'Wrzesień', value: 8 },
    { label: 'Październik', value: 9 },
    { label: 'Listopad', value: 10 },
    { label: 'Grudzień', value: 11 },
  ];
  readonly yearOptions = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, index) => currentYear - 4 + index);
  });
  readonly selectedMonth = computed(() => this.monthDate().getMonth());
  readonly selectedYear = computed(() => this.monthDate().getFullYear());
  readonly ownerUserOptions = computed(() =>
    this.workspaceUsers().map((user) => ({
      ...user,
      fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    })),
  );
  readonly workItemFilterOptions = computed<WorkItemFilterOption[]>(() => [
    { id: null, name: 'Wszystkie work itemy', clientName: null },
    ...[...this.workItems()]
      .sort((left, right) =>
        (left.name ?? '').localeCompare(right.name ?? '', 'pl', { sensitivity: 'base' }),
      )
      .map((item) => ({
        id: item.id ?? null,
        name: item.name ?? 'Bez nazwy',
        clientName: item.clientName ?? null,
      })),
  ]);
  readonly workItemsById = computed(
    () => new Map(this.workItems().map((item) => [item.id ?? '', item] as const)),
  );
  readonly filteredMonthEntries = computed(() => {
    const selectedWorkItemId = this.selectedWorkItemId();
    if (!selectedWorkItemId) {
      return this.timeEntries();
    }

    return this.timeEntries().filter((entry) => entry.workItemId === selectedWorkItemId);
  });
  readonly monthEntries = computed(() => this.sortEntriesDesc(this.filteredMonthEntries()));
  readonly monthGroups = computed(() => this.groupEntriesByDay(this.monthEntries()));
  readonly allDayGroupsCollapsed = computed(
    () =>
      this.monthGroups().length > 0 &&
      this.monthGroups().every((group) => this.collapsedDayKeys()[group.dayKey] ?? false),
  );
  readonly monthEntryCount = computed(() => this.monthEntries().length);
  readonly monthTotalDuration = computed(() =>
    this.monthEntries().reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
  );
  readonly monthTotalRevenue = computed(() =>
    this.monthEntries().reduce((acc, entry) => acc + (this.resolveEntryRevenue(entry) ?? 0), 0),
  );
  readonly monthHasThresholdEntries = computed(() =>
    this.monthEntries().some((entry) => this.isThresholdBillingEntry(entry)),
  );
  readonly monthTotalEarnings = computed(() =>
    this.monthEntries().reduce((acc, entry) => acc + (this.resolveEntryEarnings(entry) ?? 0), 0),
  );
  readonly monthRevenuePerHour = computed(() => {
    const hours = this.monthTotalDuration() / 60;
    if (!hours) {
      return null;
    }

    return this.monthTotalRevenue() / hours;
  });
  readonly selectedWorkItem = computed(
    () => this.workItems().find((item) => item.id === this.selectedWorkItemId()) ?? null,
  );
  readonly selectedWorkItemLabel = computed(() => {
    if (!this.selectedWorkItemId()) {
      return 'Wszystkie work itemy';
    }

    return this.selectedWorkItem()?.name ?? 'Nieznany work item';
  });
  readonly emptyStateTitle = computed(() =>
    this.selectedWorkItemId()
      ? 'Brak wpisów dla wybranego work itemu'
      : 'Brak wpisów w tym miesiącu',
  );
  readonly emptyStateDescription = computed(() =>
    this.selectedWorkItemId()
      ? 'Zmień filtr work itemu albo wyczyść go, aby zobaczyć wszystkie wpisy.'
      : 'Dodaj pierwszy wpis w przypiętej belce u góry.',
  );
  readonly quickAddForm = this.fb.group({
    workItemId: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    date: this.fb.control<string>(this.toLocalDateKey(new Date()), {
      validators: [Validators.required],
    }),
    start: this.fb.control<string>('08:00', {
      validators: [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)],
    }),
    end: this.fb.control<string>('09:00', {
      validators: [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)],
    }),
    revenueAmount: this.fb.control<number | null>(null, {
      validators: [Validators.required, Validators.min(0)],
    }),
  });

  private previousTimeValues: Record<'start' | 'end', string> = {
    start: '08:00',
    end: '09:00',
  };
  private readonly collapsedDayKeys = signal<Record<string, boolean>>({});
  private lastHandledWorkItemsRefresh = 0;

  constructor() {
    this.quickAddForm.controls.workItemId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateQuickAddRevenueState());

    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      if (workspaceId) {
        this.fetchWorkItems();
      }

      if (workspaceId && this.ownerMode() && this.authStore.isOwner()) {
        this.fetchWorkspaceUsers();
      }
    });

    effect(() => {
      const currentUserId = this.currentUserId();
      if (!this.selectedUserId() && currentUserId) {
        this.selectedUserId.set(currentUserId);
      }
    });

    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      const userId = this.selectedUserId();
      const month = this.monthDate();

      if (this.hydrated() && workspaceId && userId && month) {
        this.fetchMonthlyEntries();
      }
    });

    effect(() => {
      const refreshToken = this.workItemsFacadeService.refresh();
      const workspaceId = this.activeWorkspaceId();
      const userId = this.selectedUserId();
      const month = this.monthDate();

      if (
        !this.hydrated() ||
        !workspaceId ||
        !userId ||
        !month ||
        refreshToken === this.lastHandledWorkItemsRefresh
      ) {
        return;
      }

      this.lastHandledWorkItemsRefresh = refreshToken;
      this.fetchMonthlyEntries();
    });

    this.updateQuickAddRevenueState();
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const monthParam = params.get('month');
      const userIdParam = params.get('userId');
      const workItemIdParam = params.get('workItemId');

      const month = monthParam
        ? (this.parseMonthParam(monthParam) ?? this.startOfMonth(new Date()))
        : this.startOfMonth(new Date());

      if (this.formatMonthParam(this.monthDate()) !== this.formatMonthParam(month)) {
        this.monthDate.set(month);
      }

      const nextUserId = this.ownerMode()
        ? (userIdParam ?? this.currentUserId() ?? null)
        : (this.currentUserId() ?? null);
      if (this.selectedUserId() !== nextUserId) {
        this.selectedUserId.set(nextUserId);
      }

      const nextWorkItemId = workItemIdParam ?? null;
      if (this.selectedWorkItemId() !== nextWorkItemId) {
        this.selectedWorkItemId.set(nextWorkItemId);
      }

      this.quickAddForm.patchValue(
        {
          date: this.getPreferredQuickAddDate(),
        },
        { emitEvent: false },
      );

      this.hydrated.set(true);
      this.updateUrl();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected goToPreviousMonth(): void {
    const nextMonth = new Date(this.monthDate());
    nextMonth.setMonth(nextMonth.getMonth() - 1);
    this.monthDate.set(this.startOfMonth(nextMonth));
    this.quickAddForm.patchValue({ date: this.getPreferredQuickAddDate() }, { emitEvent: false });
    this.updateUrl();
  }

  protected goToNextMonth(): void {
    const nextMonth = new Date(this.monthDate());
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    this.monthDate.set(this.startOfMonth(nextMonth));
    this.quickAddForm.patchValue({ date: this.getPreferredQuickAddDate() }, { emitEvent: false });
    this.updateUrl();
  }

  protected goToCurrentMonth(): void {
    this.monthDate.set(this.startOfMonth(new Date()));
    this.quickAddForm.patchValue({ date: this.getPreferredQuickAddDate() }, { emitEvent: false });
    this.updateUrl();
  }

  protected onMonthChange(monthIndex: number | null): void {
    if (monthIndex == null || monthIndex < 0 || monthIndex > 11) {
      return;
    }

    this.setMonth(this.selectedYear(), monthIndex);
  }

  protected onYearChange(year: number | null): void {
    if (year == null || !Number.isFinite(year)) {
      return;
    }

    this.setMonth(year, this.selectedMonth());
  }

  protected onOwnerUserChange(userId: string | null): void {
    if (!userId) {
      return;
    }

    this.selectedUserId.set(userId);
    this.updateUrl();
  }

  protected onWorkItemFilterChange(workItemId: string | null): void {
    this.selectedWorkItemId.set(workItemId);
    this.updateUrl();
  }

  protected openEntry(entry: TimesheetEntryDto): void {
    const workspaceId = this.activeWorkspaceId();
    const userId = this.selectedUserId();
    if (!workspaceId || !userId || !entry.workItemId || !entry.start) {
      return;
    }

    this.modalService.open(TimeTrackerDayModalComponent, {
      workspaceId,
      userId,
      dateIso: this.toLocalDateKey(new Date(entry.start)),
      workItemId: entry.workItemId,
      workItemName: entry.workItemName,
      clientName: entry.clientName,
      workItems: this.workItems(),
      selectedEntryId: entry.id,
      onChanged: () => this.fetchMonthlyEntries(),
    });
  }

  protected toggleDayCollapsed(dayKey: string): void {
    this.collapsedDayKeys.update((current) => ({
      ...current,
      [dayKey]: !current[dayKey],
    }));
  }

  protected toggleAllDays(): void {
    if (this.monthGroups().length === 0) {
      return;
    }

    if (this.allDayGroupsCollapsed()) {
      this.expandAllDays();
      return;
    }

    this.collapseAllDays();
  }

  protected collapseAllDays(): void {
    const nextState: Record<string, boolean> = {};
    this.monthGroups().forEach((group) => {
      nextState[group.dayKey] = true;
    });

    this.collapsedDayKeys.set(nextState);
  }

  protected expandAllDays(): void {
    this.collapsedDayKeys.set({});
  }

  protected isDayCollapsed(dayKey: string): boolean {
    return this.collapsedDayKeys()[dayKey] ?? false;
  }

  protected submitQuickAdd(): void {
    if (this.quickAddForm.invalid || !this.activeWorkspaceId() || !this.selectedUserId()) {
      this.quickAddForm.markAllAsTouched();
      return;
    }

    const workspaceId = this.activeWorkspaceId()!;
    const userId = this.selectedUserId()!;
    const raw = this.quickAddForm.getRawValue();
    const date = raw.date;
    const start = raw.start;
    const end = raw.end;
    const workItemId = raw.workItemId;
    const revenueAmount = raw.revenueAmount;

    if (!date || !start || !end || !workItemId) {
      return;
    }

    if (!this.isQuickAddRevenueLocked() && revenueAmount == null) {
      return;
    }

    const entryDate = this.toLocalDate(date);
    const resolved = this.resolveTimeValuesForSave(entryDate, start, end);
    if (!resolved.duration || !resolved.endTime) {
      return;
    }

    const payload: CreateTimeEntryDto = {
      workItemId,
      duration: resolved.duration,
      revenueAmount: this.isQuickAddRevenueLocked() ? undefined : Number(revenueAmount),
      startTime: DateTimeUtils.toUTCISO(entryDate, start),
      endTime: resolved.endTime,
    };

    this.savingQuickAdd.set(true);
    this.timeEntriesService
      .createTimeEntry(workspaceId, userId, payload)
      .pipe(
        finalize(() => this.savingQuickAdd.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.quickAddForm.reset(
            {
              workItemId: null,
              date: this.getPreferredQuickAddDate(),
              start: '08:00',
              end: '09:00',
              revenueAmount: null,
            },
            { emitEvent: false },
          );
          this.previousTimeValues = { start: '08:00', end: '09:00' };
          this.updateQuickAddRevenueState();
          this.fetchMonthlyEntries();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  protected onTimeClick(controlName: 'start' | 'end', $event: Event): void {
    const control = this.quickAddForm.get(controlName);
    if (!control) {
      return;
    }

    this.previousTimeValues[controlName] = control.value ?? '';

    const input = $event.target as HTMLInputElement;
    input.value = input.value.replace(':', '');
    input.select();
  }

  protected onChangeTimeEntry(controlName: 'start' | 'end'): void {
    const control = this.quickAddForm.get(controlName);
    if (!control) {
      return;
    }

    let value = (control.value ?? '').trim().replace(':', '');
    if (!/^\d{3,4}$/.test(value)) {
      control.setValue(this.previousTimeValues[controlName], { emitEvent: false });
      return;
    }

    if (value.length === 3) {
      value = `0${value}`;
    }

    const hours = Number(value.slice(0, 2));
    const minutes = Number(value.slice(2, 4));
    if (hours > 23 || minutes > 59) {
      control.setValue(this.previousTimeValues[controlName], { emitEvent: false });
      return;
    }

    control.setValue(
      `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
      { emitEvent: false },
    );
  }

  protected formatDuration(minutes?: number | null): string {
    if (!minutes) {
      return '0 min';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} min`;
    }

    if (remainingMinutes === 0) {
      return `${hours} h`;
    }

    return `${hours} h ${remainingMinutes} min`;
  }

  protected formatMoney(value?: number | null): string {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }

  protected formatMoneyOrDash(value?: number | null): string {
    return value == null ? '--' : this.formatMoney(value);
  }

  protected resolveEntryRevenue(entry: TimesheetEntryDto): number | null {
    return entry.grossRevenue ?? entry.revenueAmount ?? null;
  }

  protected resolveEntrySummaryRevenue(entry: TimesheetEntryDto): number | null {
    if (this.isHourlyBillingEntry(entry)) {
      return this.resolveEntryEarnings(entry) ?? this.resolveEntryRevenue(entry);
    }

    return this.resolveEntryRevenue(entry);
  }

  protected resolveEntryEarnings(entry: TimesheetEntryDto): number | null {
    return entry.entryEarnings ?? entry.earnings ?? null;
  }

  protected isThresholdBillingEntry(entry: TimesheetEntryDto): boolean {
    const workItem = this.workItemsById().get(entry.workItemId ?? '');
    return workItem?.billingMode === WorkItemDto.BillingModeEnum.RevenueThreshold;
  }

  protected isHourlyBillingEntry(entry: TimesheetEntryDto): boolean {
    const workItem = this.workItemsById().get(entry.workItemId ?? '');
    return workItem?.billingMode === WorkItemDto.BillingModeEnum.HourlyRate;
  }

  protected isThresholdBillingWorkItem(workItemId?: string | null): boolean {
    if (!workItemId) {
      return false;
    }

    return (
      this.workItemsById().get(workItemId)?.billingMode ===
      WorkItemDto.BillingModeEnum.RevenueThreshold
    );
  }

  protected isHourlyBillingWorkItem(workItemId?: string | null): boolean {
    if (!workItemId) {
      return false;
    }

    return (
      this.workItemsById().get(workItemId)?.billingMode === WorkItemDto.BillingModeEnum.HourlyRate
    );
  }

  protected formatTimeRange(start?: string | null, end?: string | null): string {
    if (!start || !end) {
      return '--:--';
    }

    return `${this.formatTime(start)} - ${this.formatTime(end)}`;
  }

  protected formatDateTime(value?: string | null): string {
    if (!value) {
      return '--';
    }

    return new Date(value).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  protected trackByGroup(_: number, group: MonthEntryGroup): string {
    return group.dayKey;
  }

  protected trackByEntry(_: number, entry: TimesheetEntryDto): string | undefined {
    return entry.id;
  }

  protected get quickAddDurationLabel(): string {
    const start = this.quickAddForm.controls.start.value;
    const end = this.quickAddForm.controls.end.value;
    if (!start || !end) {
      return '0 min';
    }

    const resolved = this.resolveTimeValuesForSave(
      this.toLocalDate(this.quickAddForm.controls.date.value ?? this.getPreferredQuickAddDate()),
      start,
      end,
    );
    return this.formatDuration(resolved.duration);
  }

  protected get quickAddWorkItems(): WorkItemDto[] {
    return this.workItems();
  }

  protected get hasQuickAddItems(): boolean {
    return this.quickAddWorkItems.length > 0;
  }

  protected get quickAddDateLabel(): string {
    const date = this.quickAddForm.controls.date.value ?? this.getPreferredQuickAddDate();
    return this.toLocalDate(date).toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  protected get quickAddRevenueLabel(): string {
    const workItemId = this.quickAddForm.controls.workItemId.value;
    if (this.isHourlyBillingWorkItem(workItemId)) {
      return 'Przychód';
    }

    return this.isThresholdBillingWorkItem(workItemId) ? 'Utarg / zarobek' : 'Przychód';
  }

  protected get quickAddRevenueTooltip(): string {
    return this.isQuickAddRevenueLocked()
      ? 'Przychód będzie wyliczony na podstawie stawki godzinowej.'
      : '';
  }

  protected isQuickAddRevenueLocked(): boolean {
    return this.isHourlyBillingWorkItem(this.quickAddForm.controls.workItemId.value);
  }

  protected getCurrentMonthLabel(): string {
    return this.monthLabel();
  }

  private fetchMonthlyEntries(): void {
    const workspaceId = this.activeWorkspaceId();
    const userId = this.selectedUserId();
    const month = this.monthDate();

    if (!workspaceId || !userId || !month) {
      return;
    }

    const { start, end } = this.getMonthRangeUTC(month);
    this.loadingData.set(true);

    this.timeEntriesService
      .getTimesheet(workspaceId, userId, start.toISOString(), end.toISOString(), 0, 0, {
        start: start.toISOString(),
        end: end.toISOString(),
        page: 0,
        pageSize: 0,
      })
      .pipe(
        finalize(() => this.loadingData.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (entries) => {
          this.timeEntries.set(entries ?? []);
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private fetchWorkspaceUsers(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    if (!activeWorkspaceId) {
      return;
    }

    this.workspacesService
      .getWorkspaceUsers(activeWorkspaceId, 0, 200, ['lastName,asc', 'firstName,asc'])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.workspaceUsers.set(response.content ?? []);
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private fetchWorkItems(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    if (!activeWorkspaceId) {
      return;
    }

    if (this.ownerMode()) {
      this.workItemsService
        .getWorkItems(activeWorkspaceId, 0, 1000, ['name,asc'])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.workItems.set(response.content ?? []);
          },
          error: (err) => this.errorHandler.handleError(err),
        });
      return;
    }

    this.workItemsService
      .getWorkItemsFilter(activeWorkspaceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: WorkItemDto[]) => {
          this.workItems.set(items ?? []);
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private updateUrl(): void {
    const queryParams: Record<string, string> = {
      month: this.formatMonthParam(this.monthDate()),
    };

    const selectedUserId = this.selectedUserId();
    if (this.ownerMode() && selectedUserId) {
      queryParams['userId'] = selectedUserId;
    }

    const selectedWorkItemId = this.selectedWorkItemId();
    if (selectedWorkItemId) {
      queryParams['workItemId'] = selectedWorkItemId;
    }

    const currentParams = this.route.snapshot.queryParamMap;
    const currentMonth = currentParams.get('month');
    const currentUserId = currentParams.get('userId');
    const currentWorkItemId = currentParams.get('workItemId');
    const desiredUserId = queryParams['userId'] ?? null;
    const desiredWorkItemId = queryParams['workItemId'] ?? null;
    if (
      currentMonth === queryParams['month'] &&
      currentUserId === desiredUserId &&
      currentWorkItemId === desiredWorkItemId &&
      currentParams.keys.length === Object.keys(queryParams).length
    ) {
      return;
    }

    this.router.navigate([], {
      queryParams,
      replaceUrl: true,
    });
  }

  private sortEntriesDesc(entries: TimesheetEntryDto[]): TimesheetEntryDto[] {
    return [...entries].sort((left, right) => {
      const leftStart = left.start ? new Date(left.start).getTime() : 0;
      const rightStart = right.start ? new Date(right.start).getTime() : 0;
      return rightStart - leftStart;
    });
  }

  private groupEntriesByDay(entries: TimesheetEntryDto[]): MonthEntryGroup[] {
    const map = new Map<string, TimesheetEntryDto[]>();

    entries.forEach((entry) => {
      if (!entry.start) {
        return;
      }

      const key = this.toLocalDateKey(new Date(entry.start));
      const current = map.get(key) ?? [];
      current.push(entry);
      map.set(key, current);
    });

    return [...map.entries()]
      .sort((left, right) => right[0].localeCompare(left[0]))
      .map(([dayKey, dayEntries]) => ({
        dayKey,
        dayLabel: this.formatDayLabel(dayKey),
        dayTotalDuration: dayEntries.reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
        dayTotalRevenue: dayEntries.reduce(
          (acc, entry) => acc + (this.resolveEntrySummaryRevenue(entry) ?? 0),
          0,
        ),
        dayTotalEarnings: dayEntries.reduce(
          (acc, entry) => acc + (this.resolveEntryEarnings(entry) ?? 0),
          0,
        ),
        entries: dayEntries,
      }));
  }

  private getMonthRangeUTC(date: Date): { start: Date; end: Date } {
    const startLocal = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
    const endLocal = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

    return {
      start: DateTimeUtils.localToUTC(startLocal),
      end: DateTimeUtils.localToUTCEndDay(endLocal),
    };
  }

  private parseMonthParam(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
      return null;
    }

    return this.startOfMonth(new Date(year, month - 1, 1));
  }

  private formatMonthParam(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private setMonth(year: number, monthIndex: number): void {
    this.monthDate.set(this.startOfMonth(new Date(year, monthIndex, 1)));
    this.quickAddForm.patchValue({ date: this.getPreferredQuickAddDate() }, { emitEvent: false });
    this.updateUrl();
  }

  private getPreferredQuickAddDate(): string {
    return this.toLocalDateKey(new Date());
  }

  private updateQuickAddRevenueState(): void {
    const revenueControl = this.quickAddForm.controls.revenueAmount;
    if (this.isQuickAddRevenueLocked()) {
      revenueControl.setValue(null, { emitEvent: false });
      revenueControl.clearValidators();
      revenueControl.disable({ emitEvent: false });
    } else {
      revenueControl.enable({ emitEvent: false });
      revenueControl.setValidators([Validators.required, Validators.min(0)]);
    }

    revenueControl.updateValueAndValidity({ emitEvent: false });
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  }

  private toLocalDate(dateIso: string): Date {
    const [year, month, day] = dateIso.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private toLocalDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDayLabel(dayKey: string): string {
    return this.toLocalDate(dayKey).toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private resolveTimeValuesForSave(
    entryDate: Date,
    startValue: string,
    endValue: string,
  ): { duration: number | null; endTime: string | null } {
    const startMinutes = this.parseTimeToMinutes(startValue);
    const endMinutes = this.parseTimeToMinutes(endValue);
    if (startMinutes == null || endMinutes == null) {
      return { duration: null, endTime: null };
    }

    let durationMinutes = endMinutes - startMinutes;
    const endDate = new Date(entryDate);

    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
      endDate.setDate(endDate.getDate() + 1);
    }

    const [hours, minutes] = endValue.split(':').map(Number);
    endDate.setHours(hours, minutes, 0, 0);

    return {
      duration: durationMinutes,
      endTime: endDate.toISOString(),
    };
  }
}
